/**
 * Link rendering helpers — color, width, and visibility based on highlight state.
 */

import type { GraphLink } from '../../types/graph';

/**
 * Continuous color scale for collaboration graph edges.
 * Maps weight to a gray->steel-blue->indigo ramp with increasing opacity.
 */
// Minimum normalization ceiling so small graphs don't inflate widths/colors
const MIN_WEIGHT_CEILING = 15;

export function getCollaborationLinkColor(
  link: GraphLink,
  weightKey: string,
  maxWeight: number,
  isHighlighted: boolean,
  hasHover: boolean,
  highlightColor?: string
): string {
  if (hasHover && !isHighlighted) return 'rgba(150, 150, 170, 0.04)';
  if (hasHover && isHighlighted) return highlightColor || 'rgba(100, 100, 200, 0.7)';
  const ceiling = Math.max(maxWeight, MIN_WEIGHT_CEILING);
  const t = Math.min(((link[weightKey] as number) || 1) / ceiling, 1);
  const r = Math.round(170 - t * 100);
  const g = Math.round(170 - t * 100);
  const b = Math.round(200 - t * 30);
  const alpha = 0.25 + t * 0.55;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Normalized link width for collaboration graph.
 * Uses a minimum ceiling so that low-maxWeight graphs keep edges thin.
 */
export function getCollaborationLinkWidth(
  link: GraphLink,
  weightKey: string,
  maxWeight: number,
  isHighlighted: boolean,
  hasHover: boolean
): number {
  const ceiling = Math.max(maxWeight, MIN_WEIGHT_CEILING);
  const t = Math.min(((link[weightKey] as number) || 1) / ceiling, 1);
  const base = 1.0 + t * 8.5;
  if (!hasHover) return base;
  if (isHighlighted) return base * 2;
  return 0.3;
}

/**
 * Get link color for faculty-level views (sharedProjects key).
 */
export function getLinkColorFaculties(
  link: GraphLink,
  isHighlighted: boolean,
  hasHover: boolean,
  highlightColor?: string,
  maxSharedProjects?: number
): string {
  if (!hasHover) {
    const sp = link.sharedProjects || 0;
    if (sp > 50) return 'rgba(150, 150, 170, 0.9)';
    if (sp > 40) return 'rgba(150, 150, 170, 0.8)';
    if (sp > 30) return 'rgba(150, 150, 170, 0.7)';
    if (sp > 20) return 'rgba(150, 150, 170, 0.6)';
    if (sp > 10) return 'rgba(150, 150, 170, 0.5)';
    return 'rgba(150, 150, 170, 0.15)';
  }
  if (isHighlighted) {
    return highlightColor || 'rgba(100, 100, 200, 0.7)';
  }
  return 'rgba(150, 150, 170, 0.04)';
}

/**
 * Get link color for university-level views.
 */
export function getLinkColorUniversity(
  link: GraphLink,
  isHighlighted: boolean,
  hasHover: boolean,
  highlightColor?: string
): string {
  if (!hasHover) {
    return 'rgba(150, 150, 170, 0.15)';
  }
  if (isHighlighted) {
    return highlightColor || 'rgba(100, 100, 200, 0.7)';
  }
  return 'rgba(150, 150, 170, 0.04)';
}

/**
 * Get link width based on highlight state and weight (legacy, for non-collaboration views).
 */
export function getLinkWidth(
  link: GraphLink,
  weightKey: string,
  isHighlighted: boolean,
  hasHover: boolean
): number {
  const weight = (link[weightKey] as number) || 1;
  const base = Math.sqrt(weight) * 0.3 + 2;

  if (!hasHover) return base;
  if (isHighlighted) return base * 2.5;
  return 0.15;
}

/**
 * Determine if a link should be visible based on edge filter + highlight.
 */
export function isLinkVisible(
  link: GraphLink,
  weightKey: string,
  threshold: number,
  isHighlighted: boolean
): boolean {
  if (isHighlighted) return true;
  return ((link[weightKey] as number) || 0) >= threshold;
}
